import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useParams, Link } from 'react-router-dom';
import { blogPostsData } from '../../mocks/blog';

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPostsData.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Bold Plans Advisory`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', post.excerpt || `Read ${post.title} on the Bold Plans Advisory tax insights blog.`);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/${post.slug}`);

    const scriptId = 'schema-blog-post';
    document.getElementById(scriptId)?.remove();
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.excerpt,
      "image": post.image,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "Bold Plans Advisory"
      },
      "datePublished": post.date,
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/${post.slug}`,
      "articleSection": post.category,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/${post.slug}`
      }
    });
    document.head.appendChild(script);
    return () => { document.getElementById(scriptId)?.remove(); };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-6 py-24 text-center">
            <h1 className="font-montserrat font-bold text-4xl text-gray-900 mb-4">
              Article Not Found
            </h1>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-orange-500 font-sans font-semibold hover:gap-3 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedPosts = blogPostsData.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        <article>
          <div className="relative h-[500px] overflow-hidden">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-6 pb-12">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-orange-500 text-white px-4 py-1.5 rounded-full text-sm font-sans font-semibold">
                  {post.category}
                </span>
                {post.aiGenerated && (
                  <span className="bg-purple-600 text-white px-4 py-1.5 rounded-full text-sm font-sans font-semibold flex items-center gap-1">
                    <i className="ri-sparkling-line"></i>
                    AI-Enhanced Content
                  </span>
                )}
              </div>
              <h1 className="font-montserrat font-bold text-5xl text-white mb-4">
                {post.title}
              </h1>
              <div className="flex items-center gap-4 text-white/90 font-sans text-sm">
                <span>{post.author}</span>
                <span>•</span>
                <span>{post.date}</span>
                <span>•</span>
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-6 py-16">
            <div
              className="prose prose-lg max-w-none font-sans"
              dangerouslySetInnerHTML={{ __html: post.content }}
              style={{
                lineHeight: '1.8',
              }}
            />

            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-orange-500 font-sans font-semibold hover:gap-3 transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Blog
                </Link>
                <div className="flex items-center gap-3">
                  <span className="text-gray-600 font-sans text-sm">Share:</span>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-orange-500 hover:text-white transition-colors cursor-pointer"
                    aria-label="Share on Twitter"
                  >
                    <i className="ri-twitter-x-fill"></i>
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-orange-500 hover:text-white transition-colors cursor-pointer"
                    aria-label="Share on Facebook"
                  >
                    <i className="ri-facebook-fill"></i>
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-orange-500 hover:text-white transition-colors cursor-pointer"
                    aria-label="Share on LinkedIn"
                  >
                    <i className="ri-linkedin-fill"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedPosts.length > 0 && (
          <section className="py-16 bg-cream-50">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <h2 className="font-montserrat font-bold text-4xl text-gray-900 mb-12 text-center">
                Related Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-xs font-sans font-semibold">
                        {relatedPost.category}
                      </span>
                      <h3 className="font-montserrat font-bold text-lg text-gray-900 mt-3 mb-2 line-clamp-2 group-hover:text-orange-500 transition-colors">
                        {relatedPost.title}
                      </h3>
                      <p className="text-gray-600 font-sans text-sm line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;